import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from "react";
import { ExpandedColumn } from "library/types/columns";

import {
  ConstrainMode,
  DetailsListLayoutMode,
  IDetailsColumnRenderTooltipProps,
  IDetailsHeaderProps,
  IRenderFunction,
  Modal,
  ScrollablePane,
  Selection,
  SelectionMode,
  ShimmeredDetailsList,
  Sticky,
  StickyPositionType,
  TooltipHost,
  TooltipDelay,
  mergeStyleSets,
} from "@fluentui/react";
import { useAppTheme } from "hooks/useAppTheme";

import { useAtom } from "jotai";
import { useTranslation } from "react-i18next";
import {
  CheckboxVisibility,
  DetailsRow,
  IDetailsRowStyles,
} from "office-ui-fabric-react";
import { css } from "@emotion/css";
import {
  proposalsPageOfferItemsLoadStatusState,
  proposalsPageRowsState,
  proposalsPageSortState,
  proposalsPageTotalState,
} from "store/ProposalsPageStore";
import { useSetProposalsPageStoreValue } from "store/ProposalsPageHooks";
import { useGetTaxonomies } from "state/queries/useGetTaxonomies";
import "./ListPageTable.css";

interface ListPageTableProps {
  columns: ExpandedColumn[];
  onLoadMore: (skip: number, top: number) => void;
  onSortChange: (column: ExpandedColumn) => void;
  onSelectionChanged?: (selectedItems: any[]) => void;
}

export interface ListPageTableHandle {
  clearSelection: () => void;
}

export const ListPageTable = forwardRef<
  ListPageTableHandle,
  ListPageTableProps
>(({ columns, onLoadMore, onSortChange, onSelectionChanged }, ref) => {
  const { t } = useTranslation();
  // Don't fetch - just use cached data from App.tsx
  const { data: taxonomy } = useGetTaxonomies(false);

  const [offerItemsLoadStatus] = useAtom(
    proposalsPageOfferItemsLoadStatusState,
  );
  const [vehicleItems] = useAtom(proposalsPageRowsState);
  const [sort] = useAtom(proposalsPageSortState);
  const [count] = useAtom(proposalsPageTotalState);
  // Remove this line since we're now using the columns prop
  // const [cols] = useAtom(proposalsPageColumnsState);

  const setStoreValue = useSetProposalsPageStoreValue();

  const [isLoadingNewFiles, setIsLoadingNewFiles] = useState(false);

  // Filter to only show visible columns
  const visibleColumns = useMemo(() => {
    return columns.filter((column) => column.isVisible !== false);
  }, [columns]);

  // Initialize column widths from the passed columns instead of cols
  const [columnWidths, setColumnWidths] = useState<{ [key: string]: number }>(
    () => {
      const initialWidths: { [key: string]: number } = {};
      visibleColumns.forEach((col) => {
        if (col.width) {
          initialWidths[col.key] = col.width;
        }
      });
      return initialWidths;
    },
  );

  const theme = useAppTheme();

  // No longer load data directly - parent component manages this
  // Notify parent when sort changes
  useEffect(() => {
    if (sort) {
      if (sort.key) {
        // Find the column object to pass to parent
        const sortedColumn = columns.find((col) => col.key === sort.key);
        if (sortedColumn) {
          onSortChange(sortedColumn);
        }
      } else {
        // Sort was cleared - notify parent with empty sort
        onSortChange({ key: "", dir: "" } as any);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort?.key, sort?.dir]); // Only depend on sort changes

  // Handle column header click for sorting
  const onClickHeaderCell = useCallback(
    (column) => {
      // Use the column's isSortable property
      if (!column.isSortable) return;

      const isSorted = sort?.key === column.key;

      if (isSorted) {
        if (sort?.dir === "asc") {
          setStoreValue("sort", {
            key: column.key,
            dir: "desc",
          });
        } else {
          setStoreValue("sort", {
            key: "",
            dir: "",
          });
        }
      } else {
        setStoreValue("sort", {
          key: column.key,
          dir: "asc",
        });
      }
    },
    [sort, setStoreValue],
  );

  // Update columnsMemo to use visibleColumns instead of cols
  const columnsMemo = useMemo(() => {
    const withTranslatedHeader = visibleColumns
      .map((column: any) => {
        // Process the column using detailsListColumn function if available
        let processedColumn = { ...column };
        if (
          column.detailsListColumn &&
          typeof column.detailsListColumn === "function"
        ) {
          try {
            const detailsColumn = column.detailsListColumn({
              t,
              taxonomy: taxonomy || {},
              width: column.width,
            });
            processedColumn = { ...processedColumn, ...detailsColumn };
          } catch (error) {
            console.warn(
              "Error processing column with detailsListColumn:",
              error,
            );
          }
        }

        // Get isSortable from processedColumn (which includes detailsListColumn result)
        // Default to false for special columns
        const isSortable =
          processedColumn.isSortable !== undefined
            ? processedColumn.isSortable
            : column?.key !== "filler" && column?.key !== "options";

        // Set sort state for FluentUI to render proper icons
        const isSorted = column.key === sort?.key && isSortable;
        const isSortedDescending = isSorted && sort?.dir === "desc";

        // Convert width to FluentUI DetailsList expected properties
        const currentWidth = columnWidths[column.key] || column.width;
        return {
          ...processedColumn,
          name: t(column.labelKey),
          isSortable: isSortable,
          isSorted: isSorted,
          isSortedDescending: isSortedDescending,
          width: currentWidth,
          minWidth: Math.min(currentWidth, 50),
          maxWidth: 1000,
          isResizable: true,
          calculatedWidth: currentWidth,
        };
      })
      .concat([
        {
          key: "filler",
          label: "",
          width: 0,
          isSortable: false,
        },
      ]);
    return [...withTranslatedHeader];
  }, [sort, visibleColumns, t, taxonomy, columnWidths]); // Changed from cols to visibleColumns

  function usePrevious<T>(state: T): T | undefined {
    const ref = useRef<T>();

    useEffect(() => {
      ref.current = state;
    });

    return ref.current;
  }

  const onRenderColumnHeaderTooltip: IRenderFunction<
    IDetailsColumnRenderTooltipProps
  > = (tooltipHostProps) => {
    return (
      <TooltipHost
        {...tooltipHostProps}
        content={tooltipHostProps?.column?.name}
        delay={TooltipDelay.medium}
        styles={{
          root: {
            color: theme.isDark
              ? theme.semanticColors.bodyText
              : theme.semanticColors.bodyText,
          },
        }}
        tooltipProps={{
          styles: {
            content: {
              color: theme.isDark
                ? theme.semanticColors.bodyText
                : theme.semanticColors.bodyText,
              background: theme.isDark
                ? theme.semanticColors.menuBackground
                : theme.semanticColors.menuBackground,
            },
          },
        }}
        calloutProps={{
          styles: {
            beakCurtain: {
              background: theme.isDark
                ? theme.semanticColors.menuBackground
                : theme.semanticColors.menuBackground,
            },
            beak: {
              background: theme.isDark
                ? theme.semanticColors.menuBackground
                : theme.semanticColors.menuBackground,
            },
            calloutMain: {
              background: theme.isDark
                ? theme.semanticColors.menuBackground
                : theme.semanticColors.menuBackground,
              borderRadius: 2,
              border: theme.isDark
                ? `1px solid ${theme.semanticColors.bodyFrameDivider}`
                : `1px solid ${theme.semanticColors.bodyFrameDivider}`,
            },
          },
          beakWidth: 10,
        }}
      />
    );
  };

  const onRenderDetailsHeader = (
    props: IDetailsHeaderProps | undefined,
    defaultRender: IRenderFunction<IDetailsHeaderProps> | undefined,
  ) => (
    <Sticky
      stickyPosition={StickyPositionType.Header}
      isScrollSynced
      stickyClassName={classes.table}
    >
      <>
        {defaultRender!({
          ...props!,
          onRenderColumnHeaderTooltip,
        })}
      </>
    </Sticky>
  );

  // const onItemInvoked = (vehicle) => {
  //   const link = routes[
  //     vehicle.isTEMP ? "editTempVehicle" : "readOnlyVehicle"
  //   ].getPath(vehicle?.fleetId, vehicle?.vehicleId);
  //   history.push(link);
  // };

  const fetchMoreData = async () => {
    if (offerItemsLoadStatus === "loading" || isLoadingNewFiles) return;
    setIsLoadingNewFiles(true);
    onLoadMore(vehicleItems.length, 40);
  };

  const endReached = vehicleItems.length >= count;
  const fetchNewFiles = (ev) => {
    const element = ev.target;
    if (element && offerItemsLoadStatus !== "loading") {
      let remaningPixels =
        element.scrollHeight - element.scrollTop - element.clientHeight <= 150;
      if (remaningPixels && !endReached) {
        fetchMoreData();
      }
    }
  };

  const onSelectionChangedRef = useRef(onSelectionChanged);
  onSelectionChangedRef.current = onSelectionChanged;

  const selection = useMemo(
    () =>
      new Selection({
        onSelectionChanged: () => {
          const selected = selection.getSelection();
          onSelectionChangedRef.current?.(selected);
        },
        getKey: (item: any) => _getKey(item),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useImperativeHandle(
    ref,
    () => ({
      clearSelection: () => {
        selection.setAllSelected(false);
      },
    }),
    [selection],
  );

  const prevItemsLength = usePrevious(vehicleItems.length);
  useEffect(() => {
    if (prevItemsLength && prevItemsLength !== vehicleItems.length) {
      setIsLoadingNewFiles(false);
    }
  }, [vehicleItems, prevItemsLength]);

  // Selection is keyed by offer id so it survives reloads that reorder rows
  // (e.g. an offer jumps to the top after its invoice is marked as paid).
  const _getKey = (item: any, index?: number): string => {
    return item?.OfferId ?? item?.vehicleId ?? String(index ?? "");
  };
  const onRenderItemColumn = (
    item: any,
    index: any,
    column: any,
  ): JSX.Element | string | number => {
    // Check if column has a render function
    if (column?.render && typeof column.render === "function") {
      try {
        return column.render({ row: item, t, taxonomy: taxonomy || {} });
      } catch (error) {
        console.warn("Error executing column render function:", error);
        return item[column.key] || "";
      }
    }

    // Check if column has a transform function
    if (column?.transform && typeof column.transform === "function") {
      try {
        return column.transform({ row: item });
      } catch (error) {
        console.warn("Error executing column transform function:", error);
        return item[column.key] || "";
      }
    }

    return item[column.key] || "";
  };

  // if (!vehicleItems) return null;
  // console.log(sort);

  const onRenderRow = (props: any): JSX.Element | null => {
    const customStyles: Partial<IDetailsRowStyles> = {};
    if (props) {
      const row = props.item;
      if (!row) return null;
      if (row.IsInvalidated || row.IsMarkedAsError) {
        customStyles.root = css`
          &&&& {
            background-color: ${theme.isDark
              ? theme.palette.redDark
              : theme.semanticColors.severeWarningBackground};
          }
        `;
      }

      return <DetailsRow {...props} styles={customStyles} />;
    }
    return null;
  };

  const onColumnResize = (column?: any, newWidth?: number) => {
    if (column && newWidth !== undefined) {
      // Update local column widths state
      setColumnWidths((prev) => ({
        ...prev,
        [column.key]: newWidth,
      }));
    }
  };

  const classes = getClassNames(theme);

  return (
    <ScrollablePane
      data-help="offers-table"
      id="scrollableDiv"
      styles={{
        root: {
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: "100%",
          overflow: "hidden",
          background: theme.isDark
            ? theme.semanticColors.bodyBackground
            : theme.semanticColors.bodyBackground,
        },
        stickyAbove: {
          zIndex: 5,
        },
      }}
      onScroll={(ev) => {
        if (!endReached && offerItemsLoadStatus !== "loading") {
          fetchNewFiles(ev);
        }
      }}
    >
        <ShimmeredDetailsList
          items={vehicleItems || []}
          compact={false}
          columns={columnsMemo || []}
          selectionMode={SelectionMode.multiple}
          checkboxVisibility={CheckboxVisibility.always}
          setKey="multiple"
          getKey={_getKey}
          layoutMode={DetailsListLayoutMode.fixedColumns}
          isHeaderVisible={true}
          onColumnResize={onColumnResize}
          usePageCache={true}
          useReducedRowRenderer={true}
          className={classes.table}
          styles={{
            root: {
              height: "100% !important",
              overflowY: "visible !important",
              overflowX: "visible !important",
            },
          }}
          onRenderRow={onRenderRow}
          onRenderItemColumn={onRenderItemColumn}
          selection={selection}
          enableShimmer={
            offerItemsLoadStatus === "loading" && !isLoadingNewFiles
          }
          shimmerLines={20}
          onRenderDetailsHeader={(
            props: IDetailsHeaderProps | undefined,
            defaultRender: IRenderFunction<IDetailsHeaderProps> | undefined,
          ) => onRenderDetailsHeader(props, defaultRender)}
          selectionPreservedOnEmptyClick={false}
          enterModalSelectionOnTouch={true}
          ariaLabelForSelectionColumn="Toggle selection"
          ariaLabelForSelectAllCheckbox="Toggle selection for all items"
          checkButtonAriaLabel="Row checkbox"
          onColumnHeaderClick={(e, column) => {
            onClickHeaderCell(column);
          }}
          constrainMode={ConstrainMode.horizontalConstrained}
        />

      {/* Loading modal */}
      <Modal
        styles={{ root: { opacity: 0 } }}
        isOpen={offerItemsLoadStatus === "loading"}
        isBlocking={offerItemsLoadStatus === "loading"}
      />
    </ScrollablePane>
  );
});

const getClassNames = (theme) =>
  mergeStyleSets({
    table: {
      // EMPHASIZE MS-DETAILSLIST - Very strong styling
      ".ms-DetailsList": {
        border: "10px solid red !important",
        background: "yellow !important",
        overflow: "visible !important",
        height: "100% !important",
      },

      // Try more specific selectors for DetailsList
      "&& .ms-DetailsList": {
        border: "8px solid blue !important",
        background: "lime !important",
        overflow: "visible !important",
      },

      // Target any DetailsList deeply nested
      "div .ms-DetailsList": {
        border: "6px solid purple !important",
        background: "orange !important",
        overflow: "visible !important",
      },

      ".ms-Viewport": {
        color: "red",
      },
      // Even row background
      ".ms-List-cell:nth-child(odd) .ms-DetailsRow": {
        background: theme.isDark
          ? theme.semanticColors.bodyBackgroundHovered
          : theme.palette.themeLighterAlt,
      },

      // List cell alignment
      ".ms-List-cell": {
        display: "flex !important",
        alignItems: "center !important", // Ensure list cell content is vertically centered
      },

      // Row height and text color
      ".ms-DetailsRow ": {
        height: "30px !important",
        color: theme.isDark
          ? theme.semanticColors.bodyText
          : theme.semanticColors.bodyText,
        background: theme.isDark
          ? theme.semanticColors.bodyBackground
          : "transparent",
        display: "flex !important",
        alignItems: "center !important", // Ensure row content is vertically centered
      },

      // Hover and selected states
      ".ms-List-cell:hover .ms-DetailsRow, .ms-List-cell .ms-DetailsRow.is-selected":
        {
          background: theme.isDark
            ? theme.semanticColors.listItemBackgroundHovered + " !important"
            : theme.palette.neutralLight + " !important",
        },

      ".ms-List-cell .ms-DetailsRow:hover": {
        background: theme.isDark
          ? theme.semanticColors.listItemBackgroundHovered + " !important"
          : theme.palette.neutralLight + " !important",
      },

      // Header styling
      ".ms-DetailsHeader": {
        background: theme.isDark
          ? theme.semanticColors.bodyFrameBackground
          : theme.semanticColors.bodyFrameBackground,
        borderBottom: theme.isDark
          ? `1px solid ${theme.semanticColors.bodyFrameDivider}`
          : `1px solid ${theme.semanticColors.bodyFrameDivider}`,
      },

      ".ms-DetailsHeader-cell": {
        background: theme.isDark
          ? theme.semanticColors.bodyFrameBackground
          : theme.semanticColors.bodyFrameBackground,
      },

      ".ms-DetailsHeader-cell:hover": {
        background: theme.isDark
          ? theme.semanticColors.listHeaderBackgroundHovered
          : theme.semanticColors.listHeaderBackgroundHovered,
      },

      // Styling for sorted columns
      ".ms-DetailsHeader-cell.is-sorted": {
        background: theme.isDark
          ? theme.palette.neutralQuaternaryAlt
          : theme.palette.neutralLight,
        color: theme.semanticColors.bodyText,
        fontWeight: "600",
      },

      // Add a bottom border to sorted column header
      ".ms-DetailsHeader-cell.is-sorted::after": {
        content: "''",
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        height: "2px",
        background: theme.palette.themePrimary,
      },

      // Make sortable columns more obvious
      ".ms-DetailsHeader-cell[data-is-sortable='true']": {
        cursor: "pointer",
      },

      ".ms-DetailsHeader-cell[data-is-sortable='true'] .ms-DetailsHeader-cellName":
        {
          cursor: "pointer",
        },

      // Highlight sorted column headers
      ".ms-DetailsHeader-cell.is-sorted .ms-DetailsHeader-cellName": {
        color: theme.palette.themePrimary,
        fontWeight: 600,
      },

      // Ensure sort icons are visible
      ".ms-DetailsHeader-cell .ms-Icon": {
        color: theme.palette.themePrimary,
        //marginLeft: "4px",
      },

      ".ms-DetailsHeader-cellTitle": {
        justifyContent: "flex-start",
        color: theme.isDark
          ? theme.semanticColors.bodyText
          : theme.semanticColors.bodyText,
      },

      ".ms-DetailsHeader-cellName": {
        overflow: "visible",
        color: theme.isDark
          ? theme.semanticColors.bodyText
          : theme.semanticColors.bodyText,
      },

      // Row cell styling
      ".ms-DetailsRow": {
        borderBottom: theme.isDark
          ? `1px solid ${theme.semanticColors.bodyDivider}`
          : `1px solid ${theme.palette.neutralLighter}`,
      },

      ".ms-DetailsRow-cell": {
        display: "flex !important",
        justifyContent: "flex-start !important",
        alignItems: "center !important", // Vertical center alignment
        color: theme.isDark
          ? theme.semanticColors.bodyText
          : theme.semanticColors.bodyText,
        padding: "0 4px !important",
        minHeight: "30px !important", // Ensure consistent row height
        height: "30px !important", // Force consistent cell height
        userSelect: "text !important",
      },

      // Additional rule to ensure text content is centered
      ".ms-DetailsRow-cell > *": {
        display: "flex !important",
        alignItems: "center !important",
        minHeight: "30px !important",
      },

      // Tooltip styling for dark mode
      ".ms-Tooltip": {
        background: theme.isDark
          ? theme.semanticColors.menuBackground + " !important"
          : theme.semanticColors.menuBackground + " !important",
        color: theme.isDark
          ? theme.semanticColors.bodyText + " !important"
          : theme.semanticColors.bodyText + " !important",
        border: theme.isDark
          ? `1px solid ${theme.semanticColors.bodyFrameDivider} !important`
          : `1px solid ${theme.semanticColors.bodyFrameDivider} !important`,
      },

      ".ms-Tooltip-content": {
        background: theme.isDark
          ? theme.semanticColors.menuBackground + " !important"
          : theme.semanticColors.menuBackground + " !important",
        color: theme.isDark
          ? theme.semanticColors.bodyText + " !important"
          : theme.semanticColors.bodyText + " !important",
      },
    },
  });

export default ListPageTable;
